import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BreakoutRoomEntries,
  BreakoutRoomsSplashScreenName,
  BreakoutRoomsState,
  BreakoutRoomUserJoinedPayload,
  ConfigureBreakoutRoomPayload,
} from 'features/breakout-rooms/types';
import {
  signalingBreakoutRoomAdded,
  signalingBreakoutRoomDeleted,
  signalingBreakoutRoomRenamed,
  signalingBreakoutRoomsCleared,
  signalingBreakoutRoomsCreated,
  signalingBreakoutRoomsEdited,
  signalingBreakoutRoomsReceived,
  signalingBreakoutRoomsStarted,
  signalingBreakoutRoomsStopped,
  signalingBreakoutUserJoined,
  signalingBreakoutUserLeft,
  signalingBreakoutUserLeftBatch,
  signalingUsersAssignedToBreakoutRoom,
  signalingUserUnassignedFromBreakoutRoom,
} from 'features/breakout-rooms/actions';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { RootState } from 'store/store';
import { changeRole, selectActiveUserById, userLeft } from 'features/users/usersSlice';
import {
  signalingAvatarChanged,
  signalingAvatarDeleted,
  userRenamed,
} from 'features/users/actions';
import {
  signalingRoomJoined,
  signalingRoomLocked,
  signalingRoomUnlocked,
} from 'features/room/actions';
import { User, UserId } from 'features/users/types';
import { selectBreakoutOrUnassignedUser } from 'features/breakout-rooms/selectors/selectBreakoutOrUnassignedUser';
import { userNameDecrypted } from 'features/e2ee/actions';
import { signalingReconnectionStarted } from 'features/application/applicationSlice';
import { translateBreakoutDefaultRoomName } from 'features/breakout-rooms/utils/translateBreakoutDefaultRoomName';

export const initialState: BreakoutRoomsState = {
  mainRoomId: '',
  mainRoomLocked: false,
  breakout: false,
  movingBetweenRooms: false,
  freeMovement: false,
  backToMainRoom: false,
  isReconnecting: false,
  status: 'idle',
  splash: {
    show: false,
    openedTimestamp: 0,
    screenName: 'joining',
  },
  roomsLimit: 0,
  participantsLimit: 0,
  byId: {},
  allIds: [],
  users: {},
  roomByUser: {},
  unassignedUsers: {},
  pendingMetaUsers: {},
  timerStartedAt: null,
};

const unassignFromRoom = (
  state: BreakoutRoomsState,
  userId: UserId,
  roomId?: string,
  skipMainRoomAssignment = false
) => {
  const user = state.users[userId];
  if (!user) {
    // safeguard for attendees who receive this event in ongoing breakouts;
    return;
  }

  if (!skipMainRoomAssignment) {
    state.unassignedUsers[user.id] = user;
  }

  if (roomId) {
    const currentRoomId = state.roomByUser[userId];
    if (roomId === currentRoomId) {
      // ensure we unassign the user from the correct room
      delete state.roomByUser[userId];
      delete state.users[userId];
    }

    const room = state.byId[roomId];
    if (room) {
      const index = room.users.indexOf(userId);
      if (index > -1) {
        // mutate the object for performance
        room.users.splice(index, 1);
      }
    }
  }
};

const assignToRoom = (state: BreakoutRoomsState, user: User, roomId: string) => {
  if (!state.byId[roomId]) {
    // bypass race condition when breakoutUserJoined could be received before roomJoined
    state.pendingMetaUsers[user.id] = [roomId, user];

    return;
  }

  if (state.users[user.id]) {
    const previousRoomId = state.roomByUser[user.id];
    unassignFromRoom(state, user.id, previousRoomId);
  }

  state.users[user.id] = user;
  state.roomByUser[user.id] = roomId;

  if (!state.byId[roomId].users.includes(user.id)) {
    state.byId[roomId].users.push(user.id);
  }

  delete state.unassignedUsers[user.id];
};

export const breakoutRoomsSlice = createSlice({
  name: 'breakoutRooms',
  initialState,
  reducers: {
    roomsConfigured(state, action: PayloadAction<ConfigureBreakoutRoomPayload>) {
      const {
        normalizedState: { roomByUser, byId, users, allIds },
        unassignedUsers,
      } = action.payload;

      Object.assign(state, { byId, allIds, roomByUser, users, unassignedUsers });

      // sync user from premature breakoutUserJoined event
      const pendingMetaUsers = Object.values(state.pendingMetaUsers);
      if (pendingMetaUsers.length) {
        for (const [roomId, user] of pendingMetaUsers) {
          assignToRoom(state, user, roomId);
        }
        state.pendingMetaUsers = {};
      }
    },
    breakoutRoomsLocalized(state, action: PayloadAction<BreakoutRoomEntries>) {
      const localizedRoomEntries = action.payload;

      for (const roomId in localizedRoomEntries) {
        if (state.byId[roomId]) {
          state.byId[roomId].name = localizedRoomEntries[roomId].name;
        }
      }
    },
    cleanBreakoutRooms(state) {
      // @TODO merge it with signalingBreakoutRoomsCleared reducer?
      state.byId = {};
      state.allIds = [];
      state.users = {};
      state.roomByUser = {};
      state.unassignedUsers = {};
      state.timer = undefined;
      state.timerStartedAt = null;
    },
    roomUserJoined(state, action: PayloadAction<BreakoutRoomUserJoinedPayload>) {
      const { user, roomId } = action.payload;
      assignToRoom(state, user, roomId);
    },
    unassignedUserJoined(state, action: PayloadAction<User>) {
      state.unassignedUsers[action.payload.id] = action.payload;
    },
    splashScreenOpened(state, action: PayloadAction<BreakoutRoomsSplashScreenName>) {
      state.splash.screenName = action.payload;
      state.splash.show = true;
      state.splash.openedTimestamp = Number(new Date());
    },
    splashScreenClosed(state) {
      state.splash.show = false;
    },
    roomMovementRequested(state) {
      state.movingBetweenRooms = true;
    },
    breakoutTimerStartedAtSet(state, action: PayloadAction<number>) {
      state.timerStartedAt = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { breakoutRooms } = action.payload;
        if (breakoutRooms) {
          state.roomsLimit = breakoutRooms.roomsLimit;
          state.participantsLimit = breakoutRooms.participantsLimit;
        }
      })
      .addCase(signalingReconnectionStarted, (state) => {
        if (state.status === 'started') {
          state.isReconnecting = true;
        }
      })
      .addCase(signalingRoomJoined, (state, action) => {
        const { breakout, breakoutRooms, roomId, mainRoomId, mainRoomLocked } = action.payload;

        state.breakout = breakout;
        state.mainRoomId = mainRoomId || roomId;
        state.mainRoomLocked = mainRoomLocked ?? false;

        if (breakoutRooms) {
          state.freeMovement = breakoutRooms.freeMovement;
          state.backToMainRoom = breakoutRooms.backToMainRoom;
          state.lastBreakoutRoom = breakoutRooms.lastBreakoutRoom;

          if (breakoutRooms.timer) {
            state.timer = breakoutRooms.timer;
          }
        }

        if (breakoutRooms?.started) {
          state.status = 'started';
        } else if (state.status === 'ended') {
          state.status = 'created';
        } else {
          state.status = 'idle';
        }

        if (state.movingBetweenRooms) {
          state.movingBetweenRooms = false;
        }

        if (state.isReconnecting) {
          state.isReconnecting = false;
        }
      })
      .addCase(signalingRoomLocked, (state) => {
        state.mainRoomLocked = true;
      })
      .addCase(signalingRoomUnlocked, (state) => {
        state.mainRoomLocked = false;
      })
      .addCase(signalingBreakoutRoomsReceived, (state, action) => {
        const { started, timer, freeMovement, backToMainRoom, lastBreakoutRoom } = action.payload;

        state.freeMovement = freeMovement;
        state.backToMainRoom = backToMainRoom;
        state.lastBreakoutRoom = lastBreakoutRoom;

        if (started) {
          state.status = 'started';
        }

        if (timer) {
          state.timer = timer;
        }
      })
      .addCase(signalingBreakoutRoomsCleared, (state) => {
        state.byId = {};
        state.allIds = [];
        state.users = {};
        state.roomByUser = {};
        state.unassignedUsers = {};
        state.timer = undefined;
        state.timerStartedAt = null;
      })
      .addCase(signalingBreakoutRoomDeleted, (state, action) => {
        const room = state.byId[action.payload.id];

        for (const userId of room.users) {
          unassignFromRoom(state, userId);

          delete state.users[userId];
          delete state.roomByUser[userId];
        }

        state.allIds = state.allIds.filter((id) => id !== room.id);
        delete state.byId[room.id];
      })
      .addCase(signalingBreakoutRoomRenamed, (state, action) => {
        const room = state.byId[action.payload.id];

        if (room) {
          room.name = translateBreakoutDefaultRoomName(action.payload.name);
          room.originalName = action.payload.name;
        }
      })
      .addCase(userLeft, (state, action) => {
        // this reducer is only used to manage breakouts panel in the main room
        if (!state.breakout) {
          for (const { userId, roomId } of action.payload) {
            const currentRoomId = state.roomByUser[userId];

            if (state.status !== 'started') {
              // ensure we remove assigned users before breakouts start
              unassignFromRoom(state, userId, currentRoomId);
            } else if (currentRoomId === roomId) {
              // prevent user removal with batched userLeft event
              unassignFromRoom(state, userId, currentRoomId);
            }

            delete state.unassignedUsers[userId];
          }
        }
      })
      .addCase(signalingBreakoutRoomsCreated, (state, action) => {
        const { started, timer } = action.payload;
        state.status = started ? 'started' : 'created';

        if (timer) {
          state.timer = {
            totalSeconds: timer.totalSeconds,
            remainingSeconds: timer.totalSeconds,
            stopBreakouts: timer.stopBreakouts,
          };
        } else {
          state.timer = undefined;
        }
      })
      .addCase(signalingBreakoutRoomsEdited, (state, action) => {
        const { timer } = action.payload;
        if (timer) {
          state.timer = {
            totalSeconds: timer.totalSeconds,
            remainingSeconds: timer.totalSeconds,
            stopBreakouts: timer.stopBreakouts,
          };
        } else {
          state.timer = undefined;
        }
      })
      .addCase(signalingBreakoutRoomAdded, (state, action) => {
        const { id, name } = action.payload;

        state.byId[id] = {
          id,
          name: translateBreakoutDefaultRoomName(name),
          users: [],
          originalName: name,
        };
        state.allIds.push(id);
      })
      .addCase(signalingBreakoutRoomsStarted, (state, action) => {
        state.status = 'started';

        if (action.payload?.targetRoom) {
          state.targetRoom = {
            ...action.payload.targetRoom,
            name: translateBreakoutDefaultRoomName(action.payload.targetRoom.name),
          };
        }
      })
      .addCase(signalingBreakoutRoomsStopped, (state) => {
        state.status = 'ended';

        state.targetRoom = undefined;
        state.lastBreakoutRoom = undefined;
      })
      .addCase(signalingUsersAssignedToBreakoutRoom, (state, action) => {
        const { id: roomId, users } = action.payload;
        for (const userId of users) {
          // unassign from a previous room if any
          const user = state.users[userId];
          if (user) {
            const previousRoomId = state.roomByUser[userId];
            unassignFromRoom(state, userId, previousRoomId);
          }

          // assign to a new room
          state.roomByUser[userId] = roomId;
          state.byId[roomId].users.push(userId);

          const unassignedUser = state.unassignedUsers[userId];
          state.users[unassignedUser.id] = unassignedUser;

          delete state.unassignedUsers[userId];
        }
      })
      .addCase(signalingUserUnassignedFromBreakoutRoom, (state, action) => {
        const { id: roomId, userId } = action.payload;
        unassignFromRoom(state, userId, roomId);
      })
      .addCase(signalingBreakoutUserJoined, (state, action) => {
        const { id: roomId, user } = action.payload;
        if (roomId) {
          assignToRoom(state, user, roomId);
        } else {
          state.unassignedUsers[user.id] = user;

          delete state.users[user.id];
          delete state.roomByUser[user.id];
        }
      })
      .addCase(signalingBreakoutUserLeft, (state, action) => {
        const { id: roomId, userId } = action.payload;
        unassignFromRoom(state, userId, roomId, true);
        if (!roomId) {
          delete state.unassignedUsers[userId];
        }
      })
      .addCase(signalingBreakoutUserLeftBatch, (state, action) => {
        const { users } = action.payload;
        for (const { id: roomId, userId } of users) {
          unassignFromRoom(state, userId, roomId, true);
          if (!roomId) {
            delete state.unassignedUsers[userId];
          }
        }
      })
      .addCase(changeRole, (state, action) => {
        if (state.users[action.payload.id]) {
          state.users[action.payload.id].role = action.payload.role;
        }

        if (state.unassignedUsers[action.payload.id]) {
          state.unassignedUsers[action.payload.id].role = action.payload.role;
        }
      })
      .addCase(userRenamed, (state, action) => {
        const { id, name } = action.payload;

        if (state.users[id]) {
          state.users[id].name = name;
          state.users[id].initials = undefined;
        }

        if (state.unassignedUsers[id]) {
          state.unassignedUsers[id].name = name;
          state.unassignedUsers[id].initials = undefined;
        }
      })
      .addCase(signalingAvatarChanged, (state, action) => {
        const userId = action.payload.initiator.id;
        const { url } = action.payload.data;

        if (state.users[userId]) {
          state.users[userId].avatarUrl = url;
        }

        if (state.unassignedUsers[userId]) {
          state.unassignedUsers[userId].avatarUrl = url;
        }
      })
      .addCase(signalingAvatarDeleted, (state, action) => {
        const { userId } = action.payload;

        if (state.users[userId]) {
          state.users[userId].avatarUrl = undefined;
        }

        if (state.unassignedUsers[userId]) {
          state.unassignedUsers[userId].avatarUrl = undefined;
        }
      })
      .addCase(userNameDecrypted, (state, action) => {
        const { id, name } = action.payload;

        const breakoutUser = state.users[id];
        if (breakoutUser) {
          breakoutUser.name = name;
          breakoutUser.initials = undefined;
        }

        const unassignedUser = state.unassignedUsers[id];
        if (unassignedUser) {
          unassignedUser.name = name;
          unassignedUser.initials = undefined;
        }
      });
  },
});

export default breakoutRoomsSlice.reducer;

export const {
  roomsConfigured,
  cleanBreakoutRooms,
  roomUserJoined,
  unassignedUserJoined,
  splashScreenOpened,
  splashScreenClosed,
  roomMovementRequested,
  breakoutRoomsLocalized,
  breakoutTimerStartedAtSet,
} = breakoutRoomsSlice.actions;

export const selectMainRoomId = (state: RootState) => state.breakoutRooms.mainRoomId;

export const selectMainRoomLocked = (state: RootState) => state.breakoutRooms.mainRoomLocked;

export const selectIsBreakoutRoom = (state: RootState) => state.breakoutRooms.breakout;

export const selectBreakoutRoomsLimit = (state: RootState) => state.breakoutRooms.roomsLimit;

export const selectBreakoutUser = (state: RootState, userId: UserId) =>
  state.breakoutRooms.users[userId];

export const selectBreakoutParticipantsLimit = (state: RootState) =>
  state.breakoutRooms.participantsLimit;

export const selectBreakoutRoomsEntries = (state: RootState) => state.breakoutRooms.byId;

export const selectAllBreakoutRoomsIds = (state: RootState) => state.breakoutRooms.allIds;

export const selectBreakoutRoomsUsers = (state: RootState) => state.breakoutRooms.users;

export const selectBreakoutsUnassignedUserEntries = (state: RootState) =>
  state.breakoutRooms.unassignedUsers;

export const selectBreakoutRoom = (state: RootState, id: string) => state.breakoutRooms.byId[id];

export const selectBreakoutRoomsCreated = (state: RootState) => {
  const allIds = selectAllBreakoutRoomsIds(state);

  return allIds.length > 0;
};

export const selectBreakoutRoomsStatus = (state: RootState) => state.breakoutRooms.status;

export const selectBreakoutTargetRoom = (state: RootState) => state.breakoutRooms.targetRoom;

export const selectUserBreakoutRoomId = (state: RootState, userId: UserId) =>
  state.breakoutRooms.roomByUser[userId];

export const selectLocalUserInBreakoutRoom = (state: RootState, roomId: string) =>
  state.breakoutRooms.roomByUser[state.users.localUserId] === roomId;

export const selectBreakoutRoomHasSlots = (state: RootState, roomId: string) =>
  state.breakoutRooms.byId[roomId]
    ? state.breakoutRooms.byId[roomId].users.length < state.breakoutRooms.participantsLimit
    : false;

export const selectShowBreakoutsSplash = (state: RootState) => state.breakoutRooms.splash.show;

export const selectBreakoutsSplashOpenedAt = (state: RootState) =>
  state.breakoutRooms.splash.openedTimestamp;

export const selectBreakoutSplashScreenName = (state: RootState) =>
  state.breakoutRooms.splash.screenName;

export const selectActiveBreakoutUser = createSelector(
  [selectActiveUserById, selectBreakoutOrUnassignedUser],
  (appUser, breakoutUser) => appUser || breakoutUser
);

export const selectMovingBetweenRooms = (state: RootState) =>
  state.breakoutRooms.movingBetweenRooms;

export const selectBreakoutRoomReconnecting = (state: RootState) =>
  state.breakoutRooms.isReconnecting;

export const selectBreakoutRoomTimer = (state: RootState) => state.breakoutRooms.timer;

export const selectBreakoutRoomTimerStartedAt = (state: RootState) =>
  state.breakoutRooms.timerStartedAt;

export const selectBreakoutRoomFreeMovement = (state: RootState) =>
  state.breakoutRooms.freeMovement;

export const selectBreakoutRoomBackToMainRoom = (state: RootState) =>
  state.breakoutRooms.backToMainRoom;

export const selectLastBreakoutRoom = (state: RootState) => state.breakoutRooms.lastBreakoutRoom;
